import type { FC } from 'react';
import { memo, useCallback } from 'react';

import type { TModalProps } from '~components/Modals/types';
import { annals } from '~features/annals/hooks';
import { ReportModal } from '~features/report/ui/ReportModal';
import { useSession } from '~hooks/useSession';
import { useTranslations } from '~i18n/hooks';
import { type GeneratedMedia, generationApi } from '~services/api/generation';

export const ReportMediaModal: FC<
	TModalProps & {
		mediaId: GeneratedMedia['id'];
		onReported?: () => void;
	}
> = memo(({ open, onClose, locked, mediaId }) => {
	const t = useTranslations('Report.reportMedia');
	const reportMediaCategories = (
		(t.raw('reportMediaCategories') || []) as { value: string }[]
	).map((it) => it.value);
	const { userId } = useSession();
	const [reportAndDeleteMedia] =
		generationApi.useReportAndDeleteMediaMutation();
	const trackReportMediaSubmitted = annals.report.useReportSubmitted();

	const action = useCallback(
		async (selectedCategory: string, reportText?: string) => {
			if (!reportText) return;
			await reportAndDeleteMedia({
				reason: selectedCategory,
				reportText,
				userId,
				id: mediaId,
			});
			void trackReportMediaSubmitted({
				MediaID: mediaId,
				reason: selectedCategory,
				comment: reportText,
			});
		},
		[mediaId, reportAndDeleteMedia, trackReportMediaSubmitted, userId]
	);

	return (
		<ReportModal
			title={t('reportMediaModalTitle')}
			subtitle={t('reportMediaModalSubtitle')}
			buttonText={t('reportMediaModalButton')}
			open={open}
			locked={locked}
			categories={reportMediaCategories}
			action={action}
			onClose={onClose}
			reportCommentRequired
		/>
	);
});

ReportMediaModal.displayName = 'ReportMediaModal';
